import { AppLocale, DEFAULT_LOCALE, isSupportedLocale } from '../../i18n/config';
import { PatchMeData } from '../../services/user/user';
import { resolveCountryCode } from '../../i18n/country';

export type ProfileSex = 0 | 1 | 2 | 3;

export interface ProfileFormValues {
  first_name: string;
  last_name: string;
  country: string;
  birth_date: string;
  sex: ProfileSex;
  locale: AppLocale;
}

export type ProfilePatchChanges = Partial<Omit<PatchMeData, 'id'>>;

const normalizeString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

export const normalizeBirthDate = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? `${match[1]}-${match[2]}-${match[3]}`
    : '';
};

const normalizeSex = (value: unknown): ProfileSex => {
  const normalized = Number(value);
  return normalized === 0 || normalized === 1 || normalized === 2 || normalized === 3 ? normalized : 0;
};

export const isValidProfileSex = (value: ProfileSex): value is 1 | 2 | 3 =>
  value === 1 || value === 2 || value === 3;

export const isValidProfileBirthDate = (value: string): boolean => normalizeBirthDate(value) !== '';

export const normalizeProfileFormValues = (user: Record<string, unknown>): ProfileFormValues => ({
  first_name: normalizeString(user.first_name),
  last_name: normalizeString(user.last_name),
  country: resolveCountryCode(user.country),
  birth_date: normalizeBirthDate(user.birth_date),
  sex: normalizeSex(user.sex),
  locale: isSupportedLocale(user.locale) ? user.locale : DEFAULT_LOCALE,
});

export const buildProfileChanges = (
  original: ProfileFormValues,
  current: ProfileFormValues,
): ProfilePatchChanges => {
  const changes: ProfilePatchChanges = {};
  const normalizedCurrent = {
    ...current,
    first_name: normalizeString(current.first_name),
    last_name: normalizeString(current.last_name),
    country: resolveCountryCode(current.country),
    birth_date: normalizeBirthDate(current.birth_date) || normalizeString(current.birth_date),
  };
  const normalizedOriginalCountry = resolveCountryCode(original.country);

  if (normalizedCurrent.first_name !== original.first_name) {
    changes.first_name = normalizedCurrent.first_name;
  }
  if (normalizedCurrent.last_name !== original.last_name) {
    changes.last_name = normalizedCurrent.last_name;
  }
  if (normalizedCurrent.country !== normalizedOriginalCountry) {
    changes.country = normalizedCurrent.country;
  }
  if (normalizedCurrent.birth_date !== original.birth_date) {
    changes.birth_date = normalizedCurrent.birth_date;
  }
  if (normalizedCurrent.sex !== original.sex) changes.sex = normalizedCurrent.sex;
  if (normalizedCurrent.locale !== original.locale) {
    changes.locale = normalizedCurrent.locale;
  }

  return changes;
};

export const buildProfilePatchPayload = (
  userId: string,
  changes: ProfilePatchChanges,
): PatchMeData => ({
  id: userId,
  ...changes,
});

export const extractPersistedProfileChanges = (
  response: unknown,
  requested: ProfilePatchChanges,
): ProfilePatchChanges => {
  const responseObject = response && typeof response === 'object' ? response as Record<string, unknown> : {};
  const candidate = (responseObject.user ?? responseObject.data ?? response) as Record<string, unknown>;
  const persisted: ProfilePatchChanges = {};
  (['first_name', 'last_name', 'country', 'birth_date', 'sex', 'locale'] as const).forEach((field) => {
    if (!candidate || typeof candidate !== 'object' || !(field in candidate)) {
      if (field in requested) persisted[field] = requested[field] as never;
      return;
    }
    const value = candidate[field];
    if (field === 'birth_date') persisted.birth_date = normalizeBirthDate(value);
    else if (field === 'first_name' || field === 'last_name') persisted[field] = normalizeString(value);
    else if (field === 'country') persisted.country = resolveCountryCode(value);
    else if (field === 'sex') persisted.sex = normalizeSex(value);
    else if (isSupportedLocale(value)) persisted.locale = value;
    else if (field in requested) persisted[field] = requested[field] as never;
  });
  return persisted;
};

export const persistProfileChanges = async (
  userId: string,
  changes: ProfilePatchChanges,
  persist: (payload: PatchMeData) => Promise<unknown>,
  updateCurrentUser: (changes: ProfilePatchChanges) => void,
): Promise<ProfilePatchChanges> => {
  const response = await persist(buildProfilePatchPayload(userId, changes));
  const persisted = extractPersistedProfileChanges(response, changes);
  updateCurrentUser(persisted);
  return persisted;
};
